import _ from "lodash";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import Select from "react-select";
import {ApiUtility} from "../common/ApiUtility";
import {SelectOption, SelectOptionsGroup} from "../common/CommonUIElementTypes";
import {ConditionalRenderer} from "../common/ConditionalRenderer";
import {ConfirmDeleteButton} from "../common/ConfirmDeleteButton";
import {Constants} from "../common/Constants";
import {DateUtility} from "../common/DateUtility";
import {DispatchOrdersUtility} from "../common/DispatchOrdersUtility";
import {FormUtility} from "../common/FormUtility";
import {useModelFormEngine} from "../common/hooks/useModelFormEngine";
import {LeaveWithoutSavingWarningModal} from "../common/LeaveWithoutSavingWarningModal";
import {PillButton} from "../common/PillButton";
import {SelectUtility} from "../common/SelectUtility";

interface NewDispatchOrderFormProps {
    save: Function;
    cancel: Function;
    isNew?: boolean;
}

export let NewDispatchOrderForm = function (props: NewDispatchOrderFormProps) {
    const formEngine = useModelFormEngine(
        getInitialState(undefined),
        undefined,
        fetchSupportingData,
        validateForm,
        formStateToSaveData,
        props.save,
        Constants.dispatchOrderMessageAdditionalConfig
    );

    function getInitialState(activityDirections: any) {
        return {
            job_id: null,
            requested_by_user_id: null,
            dateString: DateUtility.jsDateToURLDate(new Date()),
            activities: [getEmptyActivity(activityDirections)],
            supportingData: null,
        };
    }

    function getEmptyActivity(activityDirections: any): any {
        let defaultActivityDirectionId: any = null;
        if (activityDirections) {
            defaultActivityDirectionId = activityDirections.find((direction: any) => direction.name === "Inbound")?.id ?? null;
        }
        return {
            id: null,
            activity_start_time: null,
            activityStartTimeJsDate: undefined,
            activity_direction_id: defaultActivityDirectionId,
            requested_activity_material_id: null,
            requested_material_amount: null,
            activity_scope_id: null,
            notes: '',
            purchase_order_number: '',
        };
    }

    function fetchSupportingData() {
        return Promise.all([
            ApiUtility.getJobsForDropdown(),
            ApiUtility.getForemenForDropdown(),
            ApiUtility.getActivityMaterials(true),
            ApiUtility.getActivityDirections(),
            ApiUtility.getActivityScopes(true),
        ]).then(([jobs, foremen, activityMaterials, activityDirections, activityScopes]) => {
            return ({
                ...getInitialState(activityDirections),
                supportingData: {
                    jobOptions: SelectUtility.modelArrayToSelectOptions(
                        jobs || [],
                        'id',
                        'label'
                    ),
                    userJobUserIdsByJobId: (jobs || []).reduce((lookup: any, job: any) => {
                        lookup[job.id] = job.user_job_user_ids || [];
                        return lookup;
                    }, {}),
                    foremanOptions: SelectUtility.modelArrayToSelectOptions(
                        foremen || [],
                        'id',
                        undefined,
                        (foreman: any) => foreman.person.name
                    ),
                    activityMaterials: activityMaterials,
                    activityMaterialOptions: SelectUtility.modelArrayToSelectOptions(
                        activityMaterials || [],
                        'id',
                        'name'
                    ),
                    activityScopeOptions: SelectUtility.modelArrayToSelectOptions(
                        activityScopes || [],
                        'id',
                        'name'
                    ),
                    activityDirections: activityDirections || [],
                },
            })
        });
    }

    function getValidationData() {
        const formState: any = formEngine.dataStore.data();
        const validationErrors: string[] = [];
        const invalidFields: string[] = [];

        if (!formState.job_id) {
            validationErrors.push('Please select a job.');
            invalidFields.push('job_id');
        }
        if (!formState.requested_by_user_id) {
            validationErrors.push('Please select a foreman.');
            invalidFields.push('requested_by_user_id');
        }
        if (!formState.dateString) {
            validationErrors.push('Please select a date.');
            invalidFields.push('dateString');
        }

        (formState.activities || []).forEach((activity: any, activityIndex: number) => {
            const fieldIndices = [`activity-${activityIndex}`];
            if (!(activity.activityStartTimeJsDate instanceof Date)) {
                validationErrors.push(`Please select a start time for activity ${activityIndex + 1}.`);
                invalidFields.push(FormUtility.getIndexedFieldName('activityStartTimeJsDate', fieldIndices));
            }
            if (!activity.activity_direction_id) {
                validationErrors.push(`Please select a direction for activity ${activityIndex + 1}.`);
                invalidFields.push(FormUtility.getIndexedFieldName('activity_direction_id', fieldIndices));
            }
            if (!activity.requested_activity_material_id) {
                validationErrors.push(`Please select a material for activity ${activityIndex + 1}.`);
                invalidFields.push(FormUtility.getIndexedFieldName('requested_activity_material_id', fieldIndices));
            }
            const amount = Number(activity.requested_material_amount);
            if (!Number.isFinite(amount) || amount <= 0) {
                validationErrors.push(`Please enter a material amount greater than 0 for activity ${activityIndex + 1}.`);
                invalidFields.push(FormUtility.getIndexedFieldName('requested_material_amount', fieldIndices));
            }
            if (!activity.activity_scope_id) {
                validationErrors.push(`Please select a scope for activity ${activityIndex + 1}.`);
                invalidFields.push(FormUtility.getIndexedFieldName('activity_scope_id', fieldIndices));
            }
        });

        return {validationErrors, invalidFields};
    }

    function validateForm(handleValidationResult: Function) {
        return new Promise(function (resolve) {
            const {validationErrors, invalidFields} = getValidationData();
            handleValidationResult(validationErrors, invalidFields, resolve);
        });
    }

    function formStateToSaveData(state: any) {
        return {
            job_id: state.job_id,
            requested_by_user_id: state.requested_by_user_id,
            dateString: state.dateString,
            activities: (state.activities || []).map((activity: any) => ({
                activity_start_time: activity.activityStartTimeJsDate?.toISOString(),
                activity_direction_id: activity.activity_direction_id,
                requested_activity_material_id: activity.requested_activity_material_id,
                requested_material_amount: activity.requested_material_amount,
                activity_scope_id: activity.activity_scope_id,
                notes: activity.notes,
                purchase_order_number: activity.purchase_order_number,
            })),
        };
    }

    function handleJobChange(option: SelectOption) {
        formEngine.updateAndRenderUnsavedData({job_id: option.value});
    }

    function handleForemanChange(option: SelectOption) {
        formEngine.updateAndRenderUnsavedData({requested_by_user_id: option.value});
    }

    function getForemanPickerOptions(): (SelectOption | SelectOptionsGroup)[] {
        const supportingData = formEngine.dataStore.data().supportingData;
        const foremanOptions: SelectOption[] = supportingData.foremanOptions || [];
        const jobId = formEngine.dataStore.data().job_id;
        if (!jobId) {
            return foremanOptions;
        }
        const userJobUserIds = (supportingData.userJobUserIdsByJobId || {})[jobId] || [];
        if (userJobUserIds.length === 0) {
            return foremanOptions;
        }
        const userJobUserIdStrings = userJobUserIds.map((id: any) => '' + id);
        const onTheJobOptions = foremanOptions.filter(
            (option) => userJobUserIdStrings.includes('' + option.value)
        );
        const otherForemanOptions = foremanOptions.filter(
            (option) => !userJobUserIdStrings.includes('' + option.value)
        );
        return [
            {label: "On the job", options: onTheJobOptions},
            {label: "Other foremen", options: otherForemanOptions},
        ];
    }

    function handleDateChange(date: Date | null) {
        formEngine.updateAndRenderUnsavedData({
            dateString: date ? DateUtility.jsDateToURLDate(date) : '',
        });
    }

    function updateActivityField(activityIndex: number, fieldName: string, value: any) {
        let updatedActivities = _.cloneDeep(formEngine.dataStore.data().activities);
        updatedActivities[activityIndex][fieldName] = value;
        if (fieldName === "activity_scope_id") {
            let selectableMaterialOptions = DispatchOrdersUtility.selectableMaterialOptionsForScope(
                formEngine.dataStore.data().supportingData.activityMaterials,
                value
            );
            let currentMaterialId = updatedActivities[activityIndex].requested_activity_material_id;
            let materialStillSelectable = selectableMaterialOptions.some(
                (option) => '' + option.value === '' + currentMaterialId
            );
            if (!materialStillSelectable) {
                updatedActivities[activityIndex].requested_activity_material_id = null;
            }
        }
        formEngine.updateAndRenderUnsavedData({activities: updatedActivities});
    }

    function handleActivityStartTimeChange(activityIndex: number, date: Date | null) {
        let updatedActivities = _.cloneDeep(formEngine.dataStore.data().activities);
        updatedActivities[activityIndex].activityStartTimeJsDate = date || undefined;
        formEngine.updateAndRenderUnsavedData({activities: updatedActivities});
    }

    function addActivity() {
        let updatedActivities = _.cloneDeep(formEngine.dataStore.data().activities);
        updatedActivities.push(getEmptyActivity(formEngine.dataStore.data().supportingData.activityDirections));
        formEngine.updateAndRenderUnsavedData({activities: updatedActivities});
    }

    function deleteActivity(activityIndex: number) {
        let updatedActivities = _.cloneDeep(formEngine.dataStore.data().activities);
        updatedActivities.splice(activityIndex, 1);
        formEngine.updateAndRenderUnsavedData({activities: updatedActivities});
    }

    function renderActivity(activity: any, activityIndex: number) {
        const supportingData = formEngine.dataStore.data().supportingData;
        const activityDirections = supportingData.activityDirections || [];
        const inboundDirection = activityDirections.find((direction: any) => direction.name === "Inbound");
        const outboundDirection = activityDirections.find((direction: any) => direction.name === "Outbound");
        const activityMaterials = supportingData.activityMaterials || [];
        const selectedMaterial = activityMaterials.find(
            (material: any) => material.id === activity.requested_activity_material_id
        );
        let amountUnitLabel = ''
        if (selectedMaterial?.measurement_unit) {
            amountUnitLabel = selectedMaterial.measurement_unit.toUpperCase() + 'S';
        }

        const showDeleteActivity = formEngine.dataStore.data().activities.length > 1;
        const inboundButtonClasses = "new-dispatch-order-form-direction-toggle-option" +
            (inboundDirection && activity.activity_direction_id === inboundDirection.id ? " selected" : "");
        const outboundButtonClasses = "new-dispatch-order-form-direction-toggle-option" +
            (outboundDirection && activity.activity_direction_id === outboundDirection.id ? " selected" : "");

        return (
            <div className="new-dispatch-order-form-activity" key={activityIndex}>
                <div className="new-dispatch-order-form-activity-header">
                    <div className="new-dispatch-order-form-activity-header-text">TRUCK ACTIVITY</div>
                    <ConditionalRenderer if={showDeleteActivity} renderer={() => (
                        <ConfirmDeleteButton
                            iconSrc="/static/images/trash-red.svg"
                            confirmingIconSrc="/static/images/trash-white.svg"
                            confirmText="CONFIRM ACTIVITY DELETION"
                            onConfirm={() => deleteActivity(activityIndex)}
                        />
                    )}/>
                </div>
                <div className="new-dispatch-order-form-activity-divider"/>
                <div className="common-form-body-row">
                    <div className="common-form-body-row-horizontal">
                        <div className="common-form-body-row-horizontal-cell">
                            <div className={formEngine.getFormFieldClasses(
                                "common-form-field",
                                FormUtility.getIndexedFieldName("activityStartTimeJsDate", [`activity-${activityIndex}`])
                            )}>
                                <div className="common-form-field-label">START TIME</div>
                                <div className="common-form-field-input-container time-picker-container">
                                    <DatePicker
                                        selected={activity.activityStartTimeJsDate}
                                        onChange={(date: Date | null) => handleActivityStartTimeChange(activityIndex, date)}
                                        showTimeSelect
                                        showTimeSelectOnly
                                        timeIntervals={15}
                                        timeCaption="Time"
                                        dateFormat="hh:mm aa"
                                        popperPlacement="bottom-start"
                                        popperModifiers={[
                                            {
                                                name: "myModifier",
                                                fn(state: any) {
                                                    state.rects.floating.width = state.rects.reference.width
                                                    state.middlewareData.arrow.x = (state.rects.floating.width / 2) - 8
                                                    return state;
                                                },
                                            },
                                        ]}
                                    />
                                    <img
                                        src="/static/images/clock-textcolor.svg"
                                        className="common-form-field-right-icon"
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="common-form-body-row-horizontal-cell">
                            <div className={formEngine.getFormFieldClasses(
                                "common-form-field",
                                FormUtility.getIndexedFieldName("activity_direction_id", [`activity-${activityIndex}`])
                            )}>
                                <div className="common-form-field-input-container">
                                    <div className="new-dispatch-order-form-direction-toggle">
                                        <button
                                            type="button"
                                            className={inboundButtonClasses}
                                            onClick={() => inboundDirection && updateActivityField(activityIndex, "activity_direction_id", inboundDirection.id)}
                                        >Inbound
                                        </button>
                                        <button
                                            type="button"
                                            className={outboundButtonClasses}
                                            onClick={() => outboundDirection && updateActivityField(activityIndex, "activity_direction_id", outboundDirection.id)}
                                        >Outbound
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="common-form-body-row">
                    <div className="common-form-body-row-horizontal">
                        <div className="common-form-body-row-horizontal-cell">
                            <div className={formEngine.getFormFieldClasses(
                                "common-form-field",
                                FormUtility.getIndexedFieldName("activity_scope_id", [`activity-${activityIndex}`])
                            )}>
                                <div className="common-form-field-label">ACTIVITY SCOPE</div>
                                <div className="common-form-field-input-container">
                                    <Select
                                        className="common-form-select"
                                        classNamePrefix="common-form-select"
                                        placeholder="Select scope"
                                        value={SelectUtility.selectOptionForId(
                                            '' + activity.activity_scope_id,
                                            formEngine.dataStore.data().supportingData.activityScopeOptions
                                        )}
                                        onChange={(option) => updateActivityField(
                                            activityIndex,
                                            "activity_scope_id",
                                            (option as SelectOption)?.value
                                        )}
                                        options={formEngine.dataStore.data().supportingData.activityScopeOptions}
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="common-form-body-row-horizontal-cell">
                            <div className={formEngine.getFormFieldClasses(
                                "common-form-field",
                                FormUtility.getIndexedFieldName("requested_activity_material_id", [`activity-${activityIndex}`])
                            )}>
                                <div className="common-form-field-label">MATERIAL</div>
                                <div className="common-form-field-input-container">
                                    <Select
                                        className="common-form-select"
                                        classNamePrefix="common-form-select"
                                        value={SelectUtility.selectOptionForId(
                                            '' + activity.requested_activity_material_id,
                                            DispatchOrdersUtility.selectableMaterialOptionsForScope(
                                                formEngine.dataStore.data().supportingData.activityMaterials,
                                                activity.activity_scope_id
                                            )
                                        )}
                                        onChange={(option) => updateActivityField(
                                            activityIndex,
                                            "requested_activity_material_id",
                                            (option as SelectOption)?.value
                                        )}
                                        options={DispatchOrdersUtility.selectableMaterialOptionsForScope(
                                            formEngine.dataStore.data().supportingData.activityMaterials,
                                            activity.activity_scope_id
                                        )}
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="common-form-body-row-horizontal-cell">
                            <div className={formEngine.getFormFieldClasses(
                                "common-form-field",
                                FormUtility.getIndexedFieldName("requested_material_amount", [`activity-${activityIndex}`])
                            )}>
                                <div className="common-form-field-label">AMOUNT</div>
                                <div className="common-form-field-input-container amount">
                                    <input
                                        type="text"
                                        className="common-form-input"
                                        value={activity.requested_material_amount ?? ''}
                                        onChange={(event) => updateActivityField(activityIndex, "requested_material_amount", event.target.value)}
                                    />
                                    <div className="new-dispatch-order-form-amount-unit-suffix">{amountUnitLabel}</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="common-form-body-row">
                    <div className="common-form-field">
                        <div className="common-form-field-label">ACTIVITY TRUCK NOTES</div>
                        <div className="common-form-field-input-container">
                            <textarea
                                className="common-form-input"
                                placeholder="Add notes for this truck group"
                                value={activity.notes || ''}
                                onChange={(event) => updateActivityField(activityIndex, "notes", event.target.value)}
                            />
                        </div>
                    </div>
                </div>
                <div className="common-form-body-row">
                    <div className="common-form-field">
                        <div className="common-form-field-label">ACTIVITY PURCHASE ORDER #</div>
                        <div className="common-form-field-input-container">
                            <input
                                type="text"
                                className="common-form-input"
                                value={activity.purchase_order_number || ''}
                                onChange={(event) => updateActivityField(
                                    activityIndex,
                                    "purchase_order_number",
                                    event.target.value.replace(/[^A-Za-z0-9-]/g, '').slice(0, 15)
                                )}
                            />
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="new-dispatch-order-form-pane common-form">
            <ConditionalRenderer
                if={formEngine.dataStore.data().supportingData}
                renderer={() => (
                    <div className="common-form-body">
                        <div className="common-form-body-row">
                            <div className="common-form-body-row-horizontal">
                                <div className="common-form-body-row-horizontal-cell">
                                    <div className={formEngine.getFormFieldClasses("common-form-field", "dateString")}>
                                        <div className="common-form-field-label">SELECT DATE</div>
                                        <div className="common-form-field-input-container job-date-picker-container">
                                            <DatePicker
                                                selected={
                                                    formEngine.dataStore.data().dateString
                                                        ? DateUtility.urlDateToJsDate(formEngine.dataStore.data().dateString)
                                                        : null
                                                }
                                                onChange={(date: Date | null) => handleDateChange(date)}
                                                popperPlacement="bottom-start"
                                                popperModifiers={[
                                                    {
                                                        name: "myModifier",
                                                        fn(state: any) {
                                                            state.middlewareData.arrow.x = (state.rects.floating.width / 2) - 8
                                                            return state;
                                                        },
                                                    },
                                                ]}
                                            />
                                            <img
                                                className="job-date-picker-container-icon"
                                                src="/static/images/calendar-textcolor.svg"
                                            />
                                        </div>
                                    </div>
                                </div>
                                <div className="common-form-body-row-horizontal-cell">
                                    <div className={formEngine.getFormFieldClasses("common-form-field", "job_id")}>
                                        <div className="common-form-field-label">SELECT JOB</div>
                                        <div className="common-form-field-input-container">
                                            <Select
                                                className="common-form-select"
                                                classNamePrefix="common-form-select"
                                                value={SelectUtility.selectOptionForId(
                                                    '' + formEngine.dataStore.data().job_id,
                                                    formEngine.dataStore.data().supportingData.jobOptions
                                                )}
                                                onChange={(option) => handleJobChange(option as SelectOption)}
                                                options={formEngine.dataStore.data().supportingData.jobOptions}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="common-form-body-row">
                            <div className={formEngine.getFormFieldClasses(
                                "common-form-field",
                                "requested_by_user_id"
                            )}>
                                <div className="common-form-field-label">SELECT FOREMAN</div>
                                <div className="common-form-field-input-container">
                                    <Select
                                        className="common-form-select"
                                        classNamePrefix="common-form-select"
                                        value={SelectUtility.selectOptionForId(
                                            '' + formEngine.dataStore.data().requested_by_user_id,
                                            getForemanPickerOptions()
                                        )}
                                        onChange={(option) => handleForemanChange(option as SelectOption)}
                                        options={getForemanPickerOptions()}
                                    />
                                </div>
                            </div>
                        </div>
                        {formEngine.dataStore.data().activities.map((activity: any, activityIndex: number) =>
                            renderActivity(activity, activityIndex)
                        )}
                        <div className="new-dispatch-order-form-bottom-actions">
                            <div className="common-form-action-buttons-row">
                                <div className="common-form-action-button-container">
                                    <PillButton
                                        containerClasses="new-dispatch-order-form-button-container"
                                        buttonClasses="solid-black-button submit-button new-dispatch-order-form-button"
                                        buttonText="SUBMIT REQUEST"
                                        onClick={formEngine.onClickSave}
                                    />
                                </div>
                                <div className="common-form-action-button-container">
                                    <PillButton
                                        containerClasses="new-dispatch-order-form-button-container"
                                        buttonClasses="black-border-button cancel-button new-dispatch-order-form-button"
                                        buttonText="CANCEL"
                                        onClick={() => props.cancel()}
                                    />
                                </div>
                            </div>
                            <div className="new-dispatch-order-form-add-activity-button-container">
                                <button
                                    type="button"
                                    className="new-dispatch-order-form-add-activity-button"
                                    onClick={addActivity}
                                >
                                    <span className="new-dispatch-order-form-add-activity-button-label">Add another
                                        activity</span>
                                    <div className="new-dispatch-order-form-add-activity-button-icon">
                                        <img src="/static/images/plus-white.svg"/>
                                    </div>
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            />
            <LeaveWithoutSavingWarningModal navigationBlocker={formEngine.navigationBlocker}/>
        </div>
    );
};
